import { Link, type LinkProps } from "@tanstack/react-router"
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const base =
  "relative w-fit rounded-none border-0 font-medium shadow-[0_0_24px_rgb(32_74_154/0.7),0_0_60px_rgb(32_74_154/0.5),0_0_100px_rgb(32_74_154/0.3)] transition-colors duration-300 focus-visible:border-0 focus-visible:ring-0 hover:bg-brand/85"

const sizes = {
  md: "h-11 px-6 text-xs",
  lg: "h-12 px-8 text-sm",
} as const

type Size = keyof typeof sizes

type Common = {
  size?: Size
  className?: string
  children: ReactNode
}

type LinkVariant = Common & {
  to: LinkProps["to"]
  onClick?: never
} & Omit<LinkProps, "to" | "className" | "children">

type ButtonVariant = Common & {
  to?: never
  ref?: Ref<HTMLButtonElement>
} & ButtonHTMLAttributes<HTMLButtonElement>

export type SharedButtonProps = LinkVariant | ButtonVariant

export function SharedButton(props: SharedButtonProps) {
  const { size = "md", className, children, ...rest } = props
  const cls = cn(base, sizes[size], className)

  if ("to" in rest && rest.to !== undefined) {
    const linkProps = rest as LinkProps
    return (
      <Button asChild className={cls}>
        <Link {...linkProps}>{children}</Link>
      </Button>
    )
  }

  const btnProps = rest as ButtonHTMLAttributes<HTMLButtonElement> & {
    ref?: Ref<HTMLButtonElement>
  }
  return (
    <Button {...btnProps} className={cls}>
      {children}
    </Button>
  )
}
